import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useLang } from "../../context/LangContext";
import { Button } from "../../components/ui/button";

export default function LogsTab() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/logs"); setItems(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const clearAll = async () => {
    setClearing(true);
    try { await api.delete("/admin/logs"); toast.success(t("Logs limpos", "Logs cleared")); setItems([]); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setClearing(false); }
  };

  const fmt = (s) => new Date(s).toLocaleString(lang === "pt" ? "pt-PT" : "en-GB");

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <h2 className="font-heading text-2xl font-bold mr-auto">{t("Registo de erros", "Error logs")}</h2>
        <Button data-testid="logs-refresh-btn" size="sm" variant="secondary" className="gap-1.5" onClick={load} disabled={loading}><RefreshCw className="h-4 w-4" />{t("Atualizar", "Refresh")}</Button>
        <Button data-testid="logs-clear-btn" size="sm" variant="ghost" className="gap-1.5 text-destructive hover:text-destructive" onClick={clearAll} disabled={clearing || items.length === 0}>
          {clearing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}{t("Limpar", "Clear")}
        </Button>
      </div>
      <p className="text-sm text-muted-foreground mb-6">{t("Falhas de geração e outros erros do sistema, com a descrição completa.", "Generation failures and other system errors, with the full description.")}</p>

      {loading ? (
        <p className="text-muted-foreground">{t("A carregar…", "Loading…")}</p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-card p-12 text-center text-muted-foreground" data-testid="logs-empty">{t("Sem erros registados. 🎉", "No errors logged. 🎉")}</div>
      ) : (
        <div className="grid gap-3">
          {items.map((e) => (
            <div key={e.id} data-testid={`log-row-${e.id}`} className="rounded-xl border border-red-500/25 bg-red-500/5 p-4">
              <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-red-500/40 bg-red-500/10 px-2 py-0.5 font-semibold text-red-300"><AlertTriangle className="h-3.5 w-3.5" />{e.context}</span>
                <span>{fmt(e.created_at)}</span>
                {e.user && <span>· {e.user}</span>}
              </div>
              <p className="mt-2 text-sm text-red-100 break-words whitespace-pre-wrap font-mono">{e.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
